<template>
  <v-container fluid class="checklist-inspection-form-view">
    <ChecklistInspectionForm
      :breadcrumbs="breadcrumbs"
      :employee="employee"
      :responsibility="responsibility"
      :dot="dot"
      :currentBadge="currentBadge"
      :readyForLevel="readyForLevel"
      :inspectionData="inspectionData"
      :filterText="filterText"
      :showNoRecords="showNoRecords"
      :showPagination="showPagination"
      :loading="tableLoading"
      :pagination="pagination"
      :chipText="chipText"
      :filterGroups="filterGroups"
      :filterType="filterType"
      :statusOptions="statusOptions"
      :groupsItems="groupsItems"
      :levelCounter="levelCounter"
      @onSearch="handleSearch"
      @onClick="handleClick"
      @removeFilter="handleRemoveFilter"
      @filterTypes="handleFilterTypes"
      @onPaginationRequest="handlePaginationRequest"
      @clearSelection="handleClearSelection"
      @onCheckedItemsChanged="handleCheckedItemsChanged"
      @onPass="handlePass"
      @onFail="showFailModal"
      @onNotApplicable="showNotApplicableModal"
      @onClose="showResetConfirmDialog"
      @badge-selected="handleBadgeSelection"
    />

    <confirmation-dialog
      :dialog="confirmDialog"
      @confirm="handleResetEvaluation"
      @cancel="confirmDialog = false"
      :icon="'alert-icon-red.svg'"
    >
      <template v-slot:title> Reset Evaluation </template>
      <template v-slot:content> Are you sure you want to Reset this evaluation? </template>
    </confirmation-dialog>

    <ReasonModal
      :showDialog="reasonModal.show"
      :title="reasonModal.config.title"
      :description="reasonModal.config.description"
      :placeHolder="reasonModal.config.placeHolder"
      :btnLabel="reasonModal.config.btnLabel"
      :btnDanger="reasonModal.config.btnDanger"
      :btnColor="reasonModal.config.btnColor"
      @onClose="closeReasonModal"
      v-model="reason"
      @onAction="handleReasonAction"
    />

    <confirmation-dialog
      :dialog="showBadgeConfirmation"
      @confirm="confirmBadgeIssue"
      @cancel="showBadgeConfirmation = false"
      :icon="'alert-icon.svg'"
    >
      <template v-slot:title> Issue Badge </template>
      <template v-slot:content>
        Are you sure you want to issue the {{ selectedBadgeLevel }} badge to
        {{ employee.firstName }} {{ employee.lastName }}?
      </template>
    </confirmation-dialog>

    <confirmation-dialog
      :dialog="showRemoveBadgeConfirmation"
      @confirm="confirmBadgeRemoval"
      @cancel="showRemoveBadgeConfirmation = false"
      :icon="'alert-icon-red.svg'"
    >
      <template v-slot:title> Remove Badge </template>
      <template v-slot:content>
        Are you sure you want to remove the {{ currentBadge }} badge from {{ employee.firstName }}
        {{ employee.lastName }}?
      </template>
    </confirmation-dialog>

    <AwardBadgeDialog
      :isVisible="showAwardBadgeDialog"
      :employee="awardBadgeEmployee"
      :level="awardBadgeLevel"
      :currentEmployee="currentEmployeeData"
      :responsibilityName="responsibility.responsibilityName"
      @close="closeAwardBadgeDialog"
      @skip="skipAwardBadge"
      @activate="activateAwardBadge"
    />
  </v-container>
</template>

<script setup>
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { ref, computed, onMounted, getCurrentInstance, watch } from "vue";
import ChecklistInspectionForm from "@components/ChecklistInspectionForm/ChecklistInspectionForm.vue";
import ConfirmationDialog from "@components/ResponsibilitiesPicker/ConfirmationDialog.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import {
  getListActiveCheckpointsEval,
  createProficiencyBadge,
  deleteResponsibilityBadge,
} from "@/api/responsibilityAPI";
import {
  failCheckPointEvaluation,
  passCheckPointEvaluation,
  notApplicableCheckPointEvaluation,
  resetCheckPointEvaluation,
  getEmployeeInspectionData,
} from "@/api";
import { proficiencyCheckpointErrorMessage } from "@/utils/proficiencyBadgeHelpers";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import { resolveAppointedDot } from "@components/utils/appointedBadge";

const { proxy } = getCurrentInstance();

const employeeId = computed(() => proxy.$route.params.employeeId);
const responsibilityId = computed(() => proxy.$route.params.responsibilityId);

const employee = ref({});
const responsibility = ref({});
const dot = ref("Good Standing");
const currentBadge = ref("Novice");
const inspectionData = ref([]);

const skeletonLoadingEnabled = ref(false);
const initialLoading = ref(true);
const tableLoading = computed(() => initialLoading.value && skeletonLoadingEnabled.value);

const allCheckpointsData = ref([]);
const filterText = ref("");
const showNoRecords = ref(false);
const showPagination = ref(false);
const pagination = ref({
  total: 0,
  page: 1,
  perPage: 10,
});
const chipText = ref(["Not Yet Passed"]);
const appliedFilters = ref(["Not Yet Passed"]);
const filterLevels = ref([]);
const filterGroups = ref([
  {
    label: "Status",
    items: [
      { label: "No Filter", value: "No Filter" },
      { label: "Not Yet Passed", value: "Not Yet Passed" },
      { label: "Only Failed", value: "Only Failed" },
      { label: "Only Not Yet Evaluated", value: "Only Not Yet Evaluated" },
      { label: "Only Reset", value: "Only Reset" },
      { label: "Only Not Applicable", value: "Only Not Applicable" },
      { label: "Only Passed", value: "Only Passed" },
    ],
  },
]);
const filterType = ref("Checkpoint");
const statusOptions = ref([]);
const groupsItems = ref([
  {
    groups: [
      {
        type: "radio",
        label: "Status",
        items: [
          { label: "No Filter", value: "no_filter" },
          { label: "Not Yet Passed", value: "not_yet_passed" },
          { label: "Only Failed", value: "failed" },
          { label: "Only Not Yet Evaluated", value: "not_yet_evaluated" },
          { label: "Only Reset", value: "reset" },
          { label: "Only Not Applicable", value: "not_applicable" },
          { label: "Only Passed", value: "passed" },
        ],
      },
    ],
  },
]);

const breadcrumbs = computed(() => [
  {
    text: "Your Employee to Inspect",
    href: "/my-stuff/coach",
    disabled: false,
  },
  {
    text: "Inspection",
    href: `/my-stuff/coach/inspection/${employeeId.value}/${responsibilityId.value}`,
    disabled: true,
  },
]);

const filteredByStatus = computed(() => {
  let resultArray = allCheckpointsData.value;

  if (appliedFilters.value.length > 0 && !appliedFilters.value.includes("No Filter")) {
    resultArray = resultArray.filter((item) => {
      const matches = appliedFilters.value.some((filter) => checkEvaluationStatus(item, filter));
      return matches;
    });
  }

  return resultArray;
});

const filteredByText = computed(() => {
  let resultArray = filteredByStatus.value;

  if (filterText.value && filterText.value.length > 0) {
    const searchText = filterText.value.toLowerCase();
    resultArray = resultArray.filter((item) => {
      if (item?.criteria) {
        return (item.criteria || "").toLowerCase().includes(searchText);
      }
      return false;
    });
  }

  return resultArray;
});

const levelCounter = computed(() => {
  const result = filteredByText.value.reduce((acc, item) => {
    const level = item.level;
    if (acc[level]) {
      acc[level]++;
    } else {
      acc[level] = 1;
    }
    return acc;
  }, {});

  return result;
});

const readyForLevel = computed(() => {
  // Get all checkpoints (not filtered)
  const checkpointsByLevel = {
    Apprentice: [],
    Professional: [],
    Coach: [],
    Master: [],
  };

  // Group all checkpoints by level
  allCheckpointsData.value.forEach((checkpoint) => {
    if (checkpointsByLevel[checkpoint.level]) {
      checkpointsByLevel[checkpoint.level].push(checkpoint);
    }
  });

  const levels = ["Master", "Coach", "Professional", "Apprentice"];

  for (const level of levels) {
    const checkpoints = checkpointsByLevel[level];

    if (checkpoints.length === 0) {
      continue;
    }

    const allComplete = checkpoints.every(
      (cp) => cp.evaluation_status === "passed" || cp.evaluation_status === "not_applicable"
    );

    if (allComplete) {
      return level;
    }
  }

  // Return "Novice" instead of null so dropdown shows "Give" options
  return "Novice";
});

const filteredByLevel = computed(() => {
  let resultArray = filteredByText.value;

  if (filterLevels.value.length > 0) {
    resultArray = resultArray.filter((checkpoint) => {
      return filterLevels.value.includes(checkpoint.level);
    });
  }

  return resultArray;
});

const filteredCheckpoints = computed(() => {
  return filteredByLevel.value;
});

const updatePaginatedData = () => {
  const startIndex = (pagination.value.page - 1) * pagination.value.perPage;
  const endIndex = startIndex + pagination.value.perPage;
  const slicedData = filteredCheckpoints.value.slice(startIndex, endIndex);

  inspectionData.value = slicedData;
};

watch(
  filteredCheckpoints,
  (newData) => {
    pagination.value.total = newData.length;
    showNoRecords.value = newData.length === 0;
    showPagination.value = newData.length > pagination.value.perPage;

    const maxPage = Math.ceil(newData.length / pagination.value.perPage) || 1;
    if (pagination.value.page > maxPage) {
      pagination.value.page = 1;
    }

    updatePaginatedData();
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    skeletonLoadingEnabled.value = (await proxy.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
  } catch {
    skeletonLoadingEnabled.value = false;
  }
  await loadInspectionData({ initial: true });
});

const loadInspectionData = async ({ initial = false } = {}) => {
  // idea-MDP-560: on the first load the table skeleton replaces the app-level loader.
  const skeletonReplacesLoader = initial && skeletonLoadingEnabled.value;
  const showLoader = () => {
    if (!skeletonReplacesLoader) proxy.$loader.show();
  };
  const hideLoader = () => {
    if (!skeletonReplacesLoader) proxy.$loader.hide();
  };

  try {
    showLoader();

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;

    if (!accountId || !empId || !respId) {
      hideLoader();
      proxy.$router.push("/my-stuff/coach");
      return;
    }

    const response = await getEmployeeInspectionData(accountId, empId, respId);
    if (!response?.data) {
      hideLoader();
      proxy.$router.push("/my-stuff/coach");
      return;
    }

    const apiData = response.data;

    currentEmployeeData.value = apiData;

    // Find the current badge from badges array
    let currentBadgeData = apiData.badge;

    // Ensure badge has id field - check multiple possible structures
    if (currentBadgeData && !currentBadgeData.id) {
      // Try to get id from different possible locations
      currentBadgeData.id = currentBadgeData.id || apiData.badge_id || apiData.badge?.id;
    }

    employee.value = {
      id: empId,
      firstName: apiData.first_name || "",
      lastName: apiData.last_name || "",
      position: apiData.primary_position?.name || "",
      photoUrl: apiData.avatar || "",
      currentEmployeeBadge: currentBadgeData || null,
    };

    console.log("🔵 Loaded employee badge data:", currentBadgeData);

    if (apiData.responsibilityAssignments) {
      const respAssignments = apiData.responsibilityAssignments;

      responsibility.value = {
        responsibilityCode: respAssignments.responsibility_code || "",
        responsibilityName: respAssignments.responsibility_name || "",
        responsibilityId: respAssignments.responsibility_id || "",
        responsibilityImportance: respAssignments.responsibility_importance || 3,
        roleCode: respAssignments.role_code || "",
        roleName: respAssignments.role_name || "",
        roleId: respAssignments.role_id || "",
        roleImportance: respAssignments.role_importance || 3,
        positionCode: respAssignments.position_code || "",
        positionName: respAssignments.position_name || "",
        positionId: respAssignments.position_id || "",
        positionImportance: respAssignments.position_importance || 3,
        positionCommitment: respAssignments.position_commitment || "Primary",
        assignedOn: respAssignments.assigned_on || null,
        newDuration: respAssignments.new_duration || 15,
        show_codes: true,
      };
    }

    // Set current badge level and dot status
    if (currentBadgeData) {
      const badgeLevel = currentBadgeData.level || "novice";
      currentBadge.value = badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1);

      const statusMap = {
        active: "Good Standing",
        good_standing: "Good Standing",
        appointed: "Appointed",
        given: "Given",
        warning: "No Dot",
        issue: "No Dot",
        obsolete: "Obsolete",
        no_dot: "No Dot",
      };
      dot.value = resolveAppointedDot(
        currentBadgeData,
        statusMap[currentBadgeData.status] || "Good Standing"
      );
    } else {
      currentBadge.value = "Novice";
      dot.value = "Good Standing";
    }

    await loadCheckpointsData(accountId, empId, respId);

    hideLoader();
  } catch (error) {
    console.error("Failed to load inspection data:", error);
    hideLoader();
    proxy.$router.push("/my-stuff/coach");
  } finally {
    if (initial) initialLoading.value = false;
  }
};

const loadCheckpointsData = async (accountId, employeeId, responsibilityId) => {
  try {
    const response = await getListActiveCheckpointsEval(accountId, responsibilityId, employeeId);

    if (response?.data && Array.isArray(response.data)) {
      allCheckpointsData.value = response.data
        .map((el) => {
          const mapped = {
            id: el.id,
            level: getProficiencyLevelName(el.proficiency_level),
            proficiency_level: el.proficiency_level,
            criteria: el.criteria || "",
            instructions: el.instructions || "",
            video: el.microlearning || "",
            status: el.status || "",
            evaluation_status: el.evaluation_status || null,
            inspectionStatus: getInspectionStatusView(el.evaluation_status),
            isPassed: el.evaluation_status === "passed",
            isFailed: el.evaluation_status === "failed",
            isNotApplicable: el.evaluation_status === "not_applicable",
            first_name: el.updated_by?.first_name || "",
            last_name: el.updated_by?.last_name || "",
            updated_on: el.updated_on || null,
            updated_by: el.updated_by || null,
            avatar: el.updated_by?.picture || "",
            failed_reason: el.evaluation_reason || "",
            not_applicable_reason: el.evaluation_reason || "",
            reset_reason: el.evaluation_reason || "",
          };
          return mapped;
        })
        .sort((a, b) => a.proficiency_level - b.proficiency_level);
    } else {
      allCheckpointsData.value = [];
      showNoRecords.value = true;
    }
  } catch (error) {
    console.error("Failed to load checkpoints data:", error);
    allCheckpointsData.value = [];
    showNoRecords.value = true;
  }
};

const getProficiencyLevelName = (levelNumber) => {
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[levelNumber] || "Apprentice";
};

const getInspectionStatusView = (evaluationStatus) => {
  const statusMap = {
    passed: "passed-evaluation",
    failed: "failed-evaluation",
    not_applicable: "notapplicable-evaluation",
    reset: "reset-pending-evaluation",
  };
  return statusMap[evaluationStatus] || "pending-evaluation";
};

const checkEvaluationStatus = (item, filter) => {
  const { evaluation_status } = item;
  let result = false;

  switch (filter) {
    case "Not Yet Passed":
      result =
        evaluation_status === null ||
        evaluation_status === "failed" ||
        evaluation_status === "reset";
      break;
    case "Only Failed":
      result = evaluation_status === "failed";
      break;
    case "Only Not Yet Evaluated":
      result = evaluation_status === null || evaluation_status === undefined;
      break;
    case "Only Passed":
      result = evaluation_status === "passed";
      break;
    case "Only Not Applicable":
      result = evaluation_status === "not_applicable";
      break;
    case "Only Reset":
      result = evaluation_status === "reset";
      break;
    case "No Filter":
      result = true;
      break;
  }

  return result;
};

const handleSearch = (searchValue) => {
  filterText.value = searchValue;
  pagination.value.page = 1;
};

const handleClick = () => {};

const handleRemoveFilter = (filter) => {
  appliedFilters.value = appliedFilters.value.filter((f) => f !== filter);
  chipText.value = chipText.value.filter((chip) => chip !== filter);
  pagination.value.page = 1;
};

const handleFilterTypes = (levelFilters) => {
  filterLevels.value = levelFilters;
  pagination.value.page = 1;
};

const handlePaginationRequest = (paginationData) => {
  if (paginationData.page) {
    pagination.value.page = paginationData.page;
  }

  updatePaginatedData();
};

const handleClearSelection = () => {
  appliedFilters.value = [];
  chipText.value = [];
  filterText.value = "";
  filterLevels.value = [];
  pagination.value.page = 1;
};

const handleCheckedItemsChanged = (checkedItems) => {
  const filterLabels = checkedItems
    .map((item) => {
      if (typeof item === "string") {
        return item;
      } else if (typeof item === "object" && item.label) {
        return item.label;
      } else if (typeof item === "object" && item.value) {
        return item.value;
      }
      return null;
    })
    .filter(Boolean);

  appliedFilters.value = filterLabels;
  chipText.value = filterLabels;
  pagination.value.page = 1;
};

// Modal state
const confirmDialog = ref(false);
const selectedCheckpoint = ref(null);
const reasonModal = ref({
  show: false,
  type: null,
  config: {},
});
const modalConfigs = {
  fail: {
    title: "Add Criteria Failed Reason",
    description: "Please provide a reason why this criteria was not met.",
    placeHolder: "Explain why the criteria was not fulfilled or provide specific feedback.",
    btnLabel: "Fail",
    btnDanger: true,
    btnColor: null,
  },
  notApplicable: {
    title: "Mark Criteria as Not Applicable",
    description: "Provide a reason why this criteria is not applicable.",
    placeHolder: "Explain why the criteria is not applicable.",
    btnLabel: "Mark as Not Applicable",
    btnDanger: false,
    btnColor: "#666666",
  },
};
const reason = ref("");

// Handle Pass
const handlePass = async (checkpointData) => {
  try {
    proxy.$loader.show();

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;
    const chkpId = checkpointData.id;

    const previousReadyLevel = readyForLevel.value;

    const dataPayload = {
      employee: empId,
      checkpoint: chkpId,
      reason: "passed",
      status: "passed",
    };

    await passCheckPointEvaluation(accountId, respId, chkpId, dataPayload);

    await loadCheckpointsData(accountId, empId, respId);

    const newReadyLevel = readyForLevel.value;
    if (newReadyLevel && newReadyLevel !== previousReadyLevel) {
      awardBadgeLevel.value = newReadyLevel;
      showAwardBadgeDialog.value = true;
    }

    proxy.$loader.hide();
  } catch (error) {
    const msg = proficiencyCheckpointErrorMessage(error) || "Error passing checkpoint";
    proxy.$notifications?.error(msg);
    console.error("Error passing checkpoint:", error);
    proxy.$loader.hide();
  }
};

// Handle Fail
const handleFail = async (checkpointData) => {
  try {
    proxy.$loader.show();

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;
    const chkpId = checkpointData.id;

    const dataPayload = {
      employee: empId,
      checkpoint: chkpId,
      reason: reason.value,
      status: "failed",
    };

    await failCheckPointEvaluation(accountId, respId, chkpId, dataPayload);

    closeReasonModal();

    await loadCheckpointsData(accountId, empId, respId);

    proxy.$loader.hide();
  } catch (error) {
    const msg = proficiencyCheckpointErrorMessage(error) || "Error failing checkpoint";
    proxy.$notifications?.error(msg);
    console.error("Error failing checkpoint:", error);
    closeReasonModal();
    proxy.$loader.hide();
  }
};

// Handle Not Applicable
const handleNotApplicable = async (checkpointData) => {
  try {
    proxy.$loader.show();

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;
    const chkpId = checkpointData.id;

    const dataPayload = {
      employee: empId,
      checkpoint: chkpId,
      reason: reason.value,
      status: "not_applicable",
    };

    await notApplicableCheckPointEvaluation(accountId, respId, chkpId, dataPayload);

    const previousReadyLevel = readyForLevel.value;

    closeReasonModal();

    await loadCheckpointsData(accountId, empId, respId);

    const newReadyLevel = readyForLevel.value;
    if (newReadyLevel && newReadyLevel !== previousReadyLevel) {
      awardBadgeLevel.value = newReadyLevel;
      showAwardBadgeDialog.value = true;
    }

    proxy.$loader.hide();
  } catch (error) {
    const msg =
      proficiencyCheckpointErrorMessage(error) || "Error marking checkpoint as not applicable";
    proxy.$notifications?.error(msg);
    console.error("Error marking checkpoint as not applicable:", error);
    closeReasonModal();
    proxy.$loader.hide();
  }
};

// Handle Reset
const handleResetEvaluation = async () => {
  try {
    proxy.$loader.show();

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;
    const chkpId = selectedCheckpoint.value.id;

    const dataPayload = {
      employee: empId,
      checkpoint: chkpId,
      reason: "Reset by coach",
      status: "reset",
    };

    await resetCheckPointEvaluation(accountId, respId, chkpId, dataPayload);

    confirmDialog.value = false;

    await loadCheckpointsData(accountId, empId, respId);

    proxy.$loader.hide();
  } catch (error) {
    console.error("Error resetting checkpoint:", error);
    confirmDialog.value = false;
    proxy.$loader.hide();
  }
};

const openReasonModal = (type) => {
  reasonModal.value.type = type;
  reasonModal.value.config = modalConfigs[type];
  reasonModal.value.show = true;
};

const closeReasonModal = () => {
  reasonModal.value.show = false;
  reasonModal.value.type = null;
  reasonModal.value.config = {};
  reason.value = "";
  selectedCheckpoint.value = null;
};

const handleReasonAction = () => {
  if (reasonModal.value.type === "fail") {
    handleFail(selectedCheckpoint.value);
  } else if (reasonModal.value.type === "notApplicable") {
    handleNotApplicable(selectedCheckpoint.value);
  }
};

const showFailModal = (checkpointData) => {
  selectedCheckpoint.value = checkpointData;
  reason.value =
    checkpointData.failed_reason && checkpointData.evaluation_status === "failed"
      ? checkpointData.failed_reason
      : "";
  openReasonModal("fail");
};

const showNotApplicableModal = (checkpointData) => {
  selectedCheckpoint.value = checkpointData;
  reason.value = "";
  openReasonModal("notApplicable");
};

const showResetConfirmDialog = (checkpointData) => {
  selectedCheckpoint.value = checkpointData;
  confirmDialog.value = true;
};

const showBadgeConfirmation = ref(false);
const selectedBadgeLevel = ref("");
const selectedBadgeData = ref(null);
const showRemoveBadgeConfirmation = ref(false);

// Award Badge Dialog state
const showAwardBadgeDialog = ref(false);
const awardBadgeLevel = ref("");
const awardBadgeEmployee = computed(() => ({
  name: `${employee.value.firstName} ${employee.value.lastName}`,
  avatar: employee.value.photoUrl || "",
}));
const currentEmployeeData = ref(null);

const getBadgeLevelByLabel = (level) => {
  if (!level || typeof level !== "string") {
    return 0;
  }
  switch (normalizeBadgeLevel(level)) {
    case "novice":
      return 0;
    case "apprentice":
      return 1;
    case "proficient":
      return 2;
    case "coach":
      return 3;
    case "architect":
      return 4;
    default:
      return 0;
  }
};

const handleBadgeSelection = (badgeData) => {
  console.log("🔵 handleBadgeSelection called with:", badgeData);
  if (!badgeData) {
    return;
  }

  // Check if this is a remove badge action
  if (badgeData.can_remove || badgeData.isRemoveAction) {
    console.log("🔴 Remove badge action detected, showing confirmation dialog");
    showRemoveBadgeConfirmation.value = true;
    return;
  }

  // Otherwise, it's an issue/award badge action
  if (!badgeData.value) {
    return;
  }

  selectedBadgeLevel.value = badgeData.label;
  selectedBadgeData.value = badgeData;
  showBadgeConfirmation.value = true;
};

const confirmBadgeIssue = async (isNotify = false) => {
  try {
    proxy.$loader.show();
    showBadgeConfirmation.value = false;

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;

    const forced = (selectedBadgeData.value.grand_label || "").toLowerCase().includes("give");

    const level = getBadgeLevelByLabel(selectedBadgeLevel.value);

    const data = {
      employee: empId,
      responsibility: respId,
      level: level,
      given: forced,
      issued_method: forced ? "given" : "awarded",
      notify: isNotify,
    };

    await createProficiencyBadge(accountId, data);

    await loadInspectionData();

    proxy.$loader.hide();
  } catch (error) {
    console.error("Error issuing badge:", error);
    showBadgeConfirmation.value = false;
    proxy.$loader.hide();
  }
};

const confirmBadgeRemoval = async () => {
  try {
    proxy.$loader.show();
    showRemoveBadgeConfirmation.value = false;

    const accountId = localStorage.getItem("currentAccountID");

    // Get the current badge ID from multiple possible locations
    const currentBadgeId =
      employee.value?.currentEmployeeBadge?.id ||
      currentEmployeeData.value?.badge?.id ||
      currentEmployeeData.value?.badge_id ||
      employee.value?.currentEmployeeBadge?.badge_id;

    if (!currentBadgeId) {
      proxy.$notifications?.error(
        "Unable to find badge ID. Please refresh the page and try again."
      );
      proxy.$loader.hide();
      return;
    }

    const payload = {
      badge: currentBadgeId,
      method: "removed",
      reason: "Badge removed by coach",
    };

    await deleteResponsibilityBadge(accountId, currentBadgeId, payload);

    await loadInspectionData();

    proxy.$loader.hide();
    proxy.$notifications?.success("Badge removed successfully");
  } catch (error) {
    proxy.$notifications?.error("Error removing badge: " + (error?.message || "Unknown error"));
    showRemoveBadgeConfirmation.value = false;
    proxy.$loader.hide();
  }
};

// Award Badge Dialog handlers
const closeAwardBadgeDialog = () => {
  showAwardBadgeDialog.value = false;
  awardBadgeLevel.value = "";
};

const skipAwardBadge = () => {
  closeAwardBadgeDialog();
};

const activateAwardBadge = async (isNotify) => {
  try {
    proxy.$loader.show();

    const accountId = localStorage.getItem("currentAccountID");
    const empId = employeeId.value;
    const respId = responsibilityId.value;

    const level = getBadgeLevelByLabel(awardBadgeLevel.value);

    const data = {
      employee: empId,
      responsibility: respId,
      level: level,
      given: false, // Always awarded, not given
      issued_method: "awarded",
      notify: isNotify,
    };

    await createProficiencyBadge(accountId, data);

    closeAwardBadgeDialog();

    await loadInspectionData();

    proxy.$loader.hide();
  } catch (error) {
    console.error("Error in activate award badge:", error);
    proxy.$loader.hide();
    closeAwardBadgeDialog();
  }
};
</script>

<style scoped>
.checklist-inspection-form-view {
  padding: 20px;
  background-color: #f9fafb;
  min-height: 100vh;
}
</style>
